
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import {
  Bell,
  Plus,
  Edit,
  Trash2,
  Mail,
  MapPin,
  Briefcase
} from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';

interface JobAlert {
  id: string;
  _id?: string;
  title: string;
  keywords: string;
  location: string;
  jobType: string;
  emailNotifications: boolean;
  isActive: boolean;
}

export function AlertsSection() {
  const [alerts, setAlerts] = useState<JobAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    title: '',
    keywords: '',
    location: '',
    jobType: '',
    emailNotifications: true
  });

  const fetchAlerts = async () => {
    try {
      const res = await apiClient.request('/alerts');
      const data = (res as any)?.data?.alerts || (res as any)?.alerts || [];
      // Normalize ID
      const normalized = data.map((a: any) => ({ ...a, id: a.id || a._id }));
      setAlerts(normalized);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
      toast.error('Failed to load job alerts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlerts();
  }, []);

  const handleToggleAlert = async (id: string, currentStatus: boolean) => {
    // Optimistic update
    setAlerts(alerts.map(alert =>
      alert.id === id ? { ...alert, isActive: !alert.isActive } : alert
    ));

    try {
      await apiClient.request(`/alerts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive: !currentStatus })
      });
      toast.success('Alert status updated');
    } catch (error) {
      // Revert on failure
      setAlerts(alerts.map(alert =>
        alert.id === id ? { ...alert, isActive: currentStatus } : alert
      ));
      toast.error('Failed to update status');
    }
  };

  const handleDeleteAlert = async (id: string) => {
    if (!confirm('Are you sure you want to delete this alert?')) return;

    try {
      await apiClient.request(`/alerts/${id}`, { method: 'DELETE' });
      setAlerts(alerts.filter(alert => alert.id !== id));
      toast.success('Alert deleted');
    } catch (error) {
      toast.error('Failed to delete alert');
    }
  };

  const handleCreateAlert = async () => {
    if (!formData.title) return toast.error('Title is required');

    try {
      const res = await apiClient.request('/alerts', {
        method: 'POST',
        body: JSON.stringify(formData)
      });

      const newAlert = (res as any)?.data?.alert || (res as any)?.alert;
      if (newAlert) {
        setAlerts([{ ...newAlert, id: newAlert.id || newAlert._id }, ...alerts]);
        setShowAddForm(false);
        setFormData({ title: '', keywords: '', location: '', jobType: '', emailNotifications: true });
        toast.success('Job alert created successfully');
      }
    } catch (error) {
      toast.error('Failed to create alert');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Job Alerts</h2>
          <p className="text-gray-600 mt-1">Get notified about jobs matching your criteria</p>
        </div>
        <Button onClick={() => setShowAddForm(!showAddForm)}>
          <Plus className="h-4 w-4 mr-2" />
          {showAddForm ? 'Cancel' : 'Create Alert'}
        </Button>
      </div>

      {/* Create Alert Form */}
      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle>Create New Job Alert</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Alert Title *</Label>
              <Input
                value={formData.title}
                onChange={e => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Senior Developer Positions"
              />
            </div>
            <div>
              <Label>Keywords</Label>
              <Input
                value={formData.keywords}
                onChange={e => setFormData({ ...formData, keywords: e.target.value })}
                placeholder="e.g., React, Node.js, Python"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Location</Label>
                <Input
                  value={formData.location}
                  onChange={e => setFormData({ ...formData, location: e.target.value })}
                  placeholder="e.g., Kathmandu"
                />
              </div>
              <div>
                <Label>Job Type</Label>
                <Input
                  value={formData.jobType}
                  onChange={e => setFormData({ ...formData, jobType: e.target.value })}
                  placeholder="e.g., Full-time"
                />
              </div>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium">Email Notifications</p>
                <p className="text-sm text-gray-500">Receive email when new jobs match</p>
              </div>
              <Switch
                checked={formData.emailNotifications}
                onCheckedChange={checked => setFormData({ ...formData, emailNotifications: checked })}
              />
            </div>
            <div className="flex gap-3">
              <Button className="flex-1" onClick={handleCreateAlert}>Create Alert</Button>
              <Button variant="outline" onClick={() => setShowAddForm(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Alerts */}
      <div className="space-y-4">
        <h3 className="font-semibold text-lg">Your Alerts ({alerts.length})</h3>
        {alerts.length === 0 && !loading ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Bell className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No job alerts</h3>
              <p className="text-gray-500 mb-4">Create alerts to get notified about matching jobs</p>
              <Button onClick={() => setShowAddForm(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Alert
              </Button>
            </CardContent>
          </Card>
        ) : (
          alerts.map((alert) => (
            <Card key={alert.id}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg text-gray-900">{alert.title}</h3>
                      {alert.isActive ? (
                        <Badge className="bg-green-100 text-green-800">Active</Badge>
                      ) : (
                        <Badge variant="outline">Inactive</Badge>
                      )}
                    </div>

                    <div className="space-y-2 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Briefcase className="h-4 w-4" />
                        <span>Keywords: {alert.keywords || 'Any'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        <span>Location: {alert.location || 'Any'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        <span>Email notifications: {alert.emailNotifications ? 'On' : 'Off'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleAlert(alert.id, alert.isActive)}
                    >
                      {alert.isActive ? 'Pause' : 'Resume'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteAlert(alert.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Email Preferences (Static for now, can be linked to user settings later) */}
      {/* 
      <Card>
        <CardHeader>
          <CardTitle>Email Preferences</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
           ... (Keep this section if needed, or remove if out of scope)
           For now I'll comment it out or leave as UI only.
        </CardContent>
      </Card> 
      */}
    </div>
  );
}
